import React from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { Button, H3, LayoutSidebar, SectionHeader, SidebarButton, SidebarHeaderTitle, SidebarTitle, Text } from 'components';
import { PoolItem } from 'components/Calculator';
import { theme, toRem } from 'design';
import { Size } from 'design/types';
import { MPContent, SidebarMobileActions } from 'screens/MakePayment/components';
import { selectCalculatorPaymentSet, selectCalculatorResults } from 'store/TipCalculator';
import { centsToDollarsDisplay } from 'utils';

interface IPoolsProps {
  onBack: () => void;
  onExpand: () => void;
  pageHeaderContent: React.ReactNode;
  sidebarOpen?: boolean;
}

const Summary = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: ${toRem(16)} ${toRem(40)};
  width: 100%;

  @media (min-width: ${theme.breakpoint.large}px) {
    padding: ${toRem(40)};
  }
`;

const MobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-bottom: ${toRem(24)};

  @media (min-width: ${theme.breakpoint.large}px) {
    display: none;
  }
`;

const SummaryContent = styled.div`
  display: flex;
  flex-flow: column nowrap;
  flex-grow: 1;

  p {
    font-size: ${toRem(14)};
  }
`;

const SummaryHeader = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin-bottom: ${toRem(11)};

  h3 {
    font-size: ${toRem(24)};
  }
`;

const SummaryTotals = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin-bottom: ${toRem(24)};
`;

const SummaryLineItem = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  width: 100%;
  margin-bottom: ${toRem(8)};

  p {
    color: ${theme.color.neutral[500]};
  }
`;

const Pools = ({ onBack, onExpand, pageHeaderContent, sidebarOpen }: IPoolsProps) => {
  const tipCalcResults = useSelector(selectCalculatorResults());
  const paymentSetDate = useSelector(selectCalculatorPaymentSet())?.date;

  const renderSummaryContent = () => {
    let houseAccount = false;
    let retainedHouse = 0;
    let retainedTotal = 0;
    let pooledTotal = 0;

    tipCalcResults.forEach((result) => {
      result.pools.forEach((pool) => {
        retainedTotal += pool.retained.total;
        pooledTotal += Math.trunc(pool.received.reduce((sum, received) => sum + received.total, 0));
      });
      result.participants?.forEach((pool) => {
        houseAccount = true;
        retainedHouse += Math.trunc(pool.received.reduce((sum, received) => sum + received.total, 0));
      });
    });

    return (
      <SummaryContent>
        <SummaryHeader>
          <H3 marginBottom={24}>{dayjs(paymentSetDate).format('MMMM Do, YYYY')}</H3>
          <Text fontWeight="medium">Totals</Text>
        </SummaryHeader>
        <SummaryTotals>
          <SummaryLineItem data-cy="pooled-total">
            <Text>Pooled Tips & Autograts</Text>
            <Text>{centsToDollarsDisplay(pooledTotal)}</Text>
          </SummaryLineItem>
          <SummaryLineItem data-cy="retained-total">
            <Text>Retained Tips & Autograts</Text>
            <Text>{centsToDollarsDisplay(retainedTotal)}</Text>
          </SummaryLineItem>
          {houseAccount && (
            <SummaryLineItem data-cy="retained-house-total">
              <Text>Retained by House</Text>
              <Text>{centsToDollarsDisplay(retainedHouse)}</Text>
            </SummaryLineItem>
          )}
          <SummaryLineItem data-cy="tips-total">
            <Text>Total Tips & Autograts</Text>
            <Text>{centsToDollarsDisplay(pooledTotal + retainedTotal + retainedHouse)}</Text>
          </SummaryLineItem>
        </SummaryTotals>
      </SummaryContent>
    );
  };

  return (
    <LayoutSidebar
      content={
        <>
          {pageHeaderContent}
          <MPContent data-cy="pools-list">
            <SectionHeader data-cy="pools-header" title="Pools" />
            {tipCalcResults.map((result, idx) => (
              <PoolItem key={`pool_item_${idx}`} calculatorResult={result} businessDay={paymentSetDate} />
            ))}
          </MPContent>
        </>
      }
      sidebar={
        <Summary data-cy="pools-summary">
          <MobileHeader>
            <SidebarHeaderTitle>
              <SidebarButton onClick={onExpand} isOpen={sidebarOpen} />
              <SidebarTitle title="Pools Details" />
            </SidebarHeaderTitle>
            <SidebarMobileActions columns={2}>
              <Button data-cy="mobile-pools-back-button" label="Back" type="button" appearance="white" size="small" onClick={onBack} />
            </SidebarMobileActions>
          </MobileHeader>
          {renderSummaryContent()}
          <Button data-cy="pools-back-button" label="Back" type="button" appearance="white" size={Size.LARGE} onClick={onBack} />
        </Summary>
      }
      sidebarOpen={sidebarOpen}
    />
  );
};

export default Pools;
